import { z } from 'zod';
// =============================================================================
// Bank account type - enum, printed-text mapper
// =============================================================================
//
// `BankAccount.bankAccountType` (an account WE pay into - the firm's own or a
// supplier's, captured as our record of it) is the ENUM below: it is the value
// that could ever drive behaviour (an EFT beneficiary file cares about it), so
// it must be one of a closed set. Two sibling surfaces stay free text on
// purpose and must never be narrowed to this enum:
//
//   - The extraction's own `extractedData.bankAccountType` (what a vision model
//     read off a page) - an enum would have to reject an unexpected value
//     rather than record what the model actually saw.
//   - `SupplierInvoice`'s per-document snapshot column - a frozen transcription
//     of what that document printed, not our record; see the column comment in
//     supplier-invoice.prisma.
//
// `mapPrintedBankAccountType` is the one boundary where a transcribed string is
// allowed to become this enum. Both the backend (bank-account domain) and the
// Bill UI must go through it rather than hand-rolling their own mapping.
export const BankAccountTypeSchema = z.enum(['CHEQUE', 'CURRENT', 'SAVINGS', 'TRANSMISSION', 'OTHER']);
/**
 * Map an account type as printed on a bank statement or invoice onto the
 * closed `BankAccountType` enum. Case- and whitespace-insensitive; matches on
 * the presence of the SA banking keyword rather than requiring an exact
 * string, so "Business Cheque", "Cheque Account", and "Cheque/Current" all
 * resolve the same as a bare "Cheque".
 *
 * `null`/empty input returns `null` ("nothing was printed"). An unrecognised
 * NON-EMPTY value returns `'OTHER'` rather than `null`: the document DID state
 * a type, so treating it as "not captured" would silently discard that the
 * field was populated. `'OTHER'` preserves "a type was printed, we could not
 * classify it" as a state distinct from both a clean match and an absent
 * field - the same distinction `sanitiseBankBranchCode` deliberately does NOT
 * make (a malformed branch code has no analogous "other" to fall back to,
 * since a branch code is either a valid six digits or it isn't).
 *
 * A slash-joined value ("Cheque/Current") is a real printed variant - some SA
 * banks label a business transactional account either way interchangeably.
 * Keyword order below is deliberately CHEQUE before CURRENT so such a value
 * resolves to CHEQUE, matching the more common of the two labels in the seed
 * fixtures and on real invoices.
 */
export function mapPrintedBankAccountType(printed) {
    if (printed === null)
        return null;
    const normalised = printed.trim().toLowerCase();
    if (normalised === '')
        return null;
    if (normalised.includes('cheque'))
        return 'CHEQUE';
    if (normalised.includes('current'))
        return 'CURRENT';
    if (normalised.includes('saving'))
        return 'SAVINGS';
    if (normalised.includes('transmission'))
        return 'TRANSMISSION';
    return 'OTHER';
}
// =============================================================================
// Bank account identifiers - regex, sanitiser
// =============================================================================
//
// Shared so the wire schema for the firm's own bank accounts and the extraction
// path that records a SUPPLIER's printed banking details agree on what a valid
// branch code is. Mirrors the shape of `vat.ts` (regex + sanitiser + form
// schema), for the same reason: a second inline copy of the pattern is how the
// two surfaces drift.
/**
 * South African bank branch code: exactly six digits (e.g. 250655).
 *
 * Universal branch codes mean many invoices omit the code entirely, which is why
 * absence is normal rather than an error - see `sanitiseBankBranchCode`.
 */
export const SA_BRANCH_CODE_REGEX = /^\d{6}$/;
/**
 * Sanitise a printed branch code to its canonical six digits, or null.
 *
 * Returns null - rather than throwing or passing the value through - when the
 * input is not six digits after whitespace is stripped. That is the right shape
 * for a value transcribed off a document by a vision model: an unreadable or
 * foreign code should leave the field empty on the stored bank account while the
 * as-printed observation survives in the extraction's own record, not fail the
 * whole capture or write a value that later payment tooling would trust.
 */
export function sanitiseBankBranchCode(input) {
    if (input === null)
        return null;
    const stripped = input.replace(/\s/g, '');
    return SA_BRANCH_CODE_REGEX.test(stripped) ? stripped : null;
}
